import { defineAppConfig } from "@app/shared";

/**
 * Configuration de l'app. C'est le seul fichier par lequel une app se decrit :
 * ni le backend, ni le frontend, ni les Dockerfiles ne se modifient pour changer
 * une marque, un domaine ou une duree de conservation.
 *
 * Ce qui ne peut pas vivre ici parce que c'est secret (mots de passe, jetons, cles)
 * vit dans le .env de l'hote, jamais dans git.
 */
export const appConfig = defineAppConfig({
  slug: "timeline",
  name: "Ma timeline",
  description:
    "Les jalons d'une vie sur une seule frise, partagés point par point avec les proches.",
  domain: "timeline.petitlab.fr",
  contactEmail: "lab@petitlab.fr",
  locale: "fr-FR",
  // La frise horizontale tient toute une vie en largeur : colonne large (planche a 1 280 px).
  layout: { width: "wide" },
  features: {
    auth: true,
    billing: false,
    llm: false,
  },
  // Durees de conservation RGPD, en jours. Elles sont affichees telles quelles dans
  // la politique de confidentialite : les changer change la page.
  retention: {
    accountDays: 1095,
    analyticsDays: 395,
    logDays: 30,
    attributionDays: 30,
  },
  /**
   * Identite publiee dans les pages legales. Le template ne peut pas la connaitre :
   * elle engage une personne reelle. Ces valeurs arrivent vides **a dessein** et se voient
   * (avertissement en tete des pages legales, liste dans `/metrics`, avertissement dans
   * les journaux au demarrage). A remplir avant la mise en ligne de l'app.
   */
  legal: {
    // Identite de l'editeur decidee pour tout le Lab le 3 octobre 2026, recopiee depuis
    // apps/lab/app.config.ts (spec docs/idees/timeline-vie/05-spec.md, section 3.8).
    publisher: {
      name: "Nicolas DODEMANT",
      legalForm: "Entrepreneur individuel (EI), micro-entreprise",
      address: "5 bis allée de Champlong, 37210 Rochecorbon, France",
      phone: "+33 6 29 10 33 56",
      // Vide = l'adresse de contact de l'app ci-dessus est publiee.
      email: "nicolas.dodemant@gmail.com",
      registration: "SIREN 753 448 695, inscrit au Registre national des entreprises",
      // Vide si l'editeur n'est pas assujetti a la TVA.
      vatNumber: "",
      // Exige des que l'app vend. Franchise en base : "TVA non applicable, article 293 B du CGI".
      vatNotice: "TVA non applicable, article 293 B du CGI",
      publicationDirector: "Nicolas DODEMANT",
    },
    // Serveur dedie fortress, loue a OneProvider et installe a Paris (coordonnees lues sur
    // oneprovider.com/contact le 2026-10-03).
    host: {
      name: "OneProvider (BrainStorm Network Inc.)",
      address: "3275 avenue Francis-Hughes, Laval (Québec) H7L 5A5, Canada",
      phone: "+1 514 286 0253",
    },
    // Mediateur de la consommation : obligatoire des que l'app vend a des particuliers.
    mediator: {
      name: "",
      address: "",
      url: "",
    },
    // Date de derniere mise a jour des pages legales, au format AAAA-MM-JJ.
    updatedAt: "2026-11-02",
    // La frise elle-meme : ce que l'editeur y ecrit et ce que ses proches y proposent
    // (spec, section 7). Usage strictement personnel et familial.
    processing: [
      {
        collected:
          "À chaque connexion, l'adresse IP et le navigateur (user-agent) de l'appareil sont gardés avec la session.",
        purpose:
          "Sécuriser le compte et limiter les abus : ces données sont nécessaires au fonctionnement de la connexion.",
        retention:
          "Le temps de la session : 7 jours, renouvelés tant que vous vous servez du service. Elles sont effacées à la déconnexion et à la suppression du compte. Une session expirée n'est effacée qu'au prochain passage du même navigateur : sans retour de sa part, sa ligne reste en base jusqu'à la suppression du compte.",
      },
      {
        collected:
          "La frise : les moments et les périodes que l'éditeur y note (titre, date, note courte, piste), sa date de naissance, les adresses e-mail des proches qu'il invite, et les souvenirs que ces proches lui proposent. Rien ne s'affiche sans connexion, et seules l'adresse de l'éditeur et celles qu'il a invitées peuvent se connecter.",
        purpose:
          "Tenir la frise de la vie de l'éditeur et la partager, point par point, avec les proches qu'il choisit : usage strictement personnel et familial.",
        retention:
          "La frise est gardée tant que l'éditeur la garde, et il peut l'exporter ou l'effacer à tout moment depuis ses réglages. Un proche retiré, ou qui supprime son accès, perd tout de suite son adresse invitée et ses propositions en attente ; un souvenir déjà accepté reste sur la frise, sans son nom.",
      },
    ],
  },
});
